import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("no_session");
      return;
    }
    pollStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const res = await fetch(`${API}/checkout-status/${sid}`);
      if (!res.ok) throw new Error("Failed to check status");
      const data = await res.json();
      setPaymentData(data);

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      setTimeout(() => pollStatus(sid, attempts + 1), pollInterval);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5"
      style={{ backgroundColor: "#1e1e1f" }}
    >
      <div
        className="max-w-md w-full rounded-xl p-8 text-center"
        style={{
          backgroundColor: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
        data-testid="success-page"
      >
        {status === "checking" && (
          <>
            <Loader2
              className="w-12 h-12 mx-auto mb-4 animate-spin"
              style={{ color: "#7C3AED" }}
            />
            <h1
              className="text-2xl font-medium mb-2"
              style={{ fontFamily: "'Fraunces', serif", color: "#e0e0e0" }}
            >
              Processing your payment...
            </h1>
            <p
              className="text-sm"
              style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
            >
              Hang tight. This only takes a moment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle
              className="w-14 h-14 mx-auto mb-4"
              style={{ color: "#22c55e" }}
            />
            <h1
              className="text-2xl font-medium mb-2"
              style={{ fontFamily: "'Fraunces', serif", color: "#e0e0e0" }}
              data-testid="success-title"
            >
              You are in. Your Soft Reset is ready.
            </h1>
            <p
              className="text-sm mb-6"
              style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
            >
              Check your email for the download link. If you do not see it in a
              few minutes, check your spam folder.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}
              data-testid="back-to-home-link"
            >
              Back to Home
            </Link>
          </>
        )}

        {(status === "error" || status === "timeout" || status === "expired" || status === "no_session") && (
          <>
            <XCircle
              className="w-14 h-14 mx-auto mb-4"
              style={{ color: "#ef4444" }}
            />
            <h1
              className="text-2xl font-medium mb-2"
              style={{ fontFamily: "'Fraunces', serif", color: "#e0e0e0" }}
            >
              {status === "expired"
                ? "Session expired"
                : status === "no_session"
                ? "No order found"
                : "Something went wrong"}
            </h1>
            <p
              className="text-sm mb-6"
              style={{ color: "#b0b0b5", fontFamily: "'Inter', sans-serif" }}
            >
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : status === "no_session"
                ? "It looks like you got here by accident. Head back to the main page to get your Soft Reset."
                : "We could not verify your payment. If you were charged, please contact support."}
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}
              data-testid="try-again-link"
            >
              Try Again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
